import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosConfig';
import { logout } from './authSlice';

// External microservice for refreshing visuals
const REFRESH_SERVICE_URL = 'https://dashboard-refresh.onrender.com/refresh/visual';

// 1. Create a new dashboard
export const createDashboard = createAsyncThunk(
  'dashboard/createDashboard',
  async ({ user_id, title, description }, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.post('/dashboard/create', {
        user_id,
        title,
        description
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return resp.data;
    } catch (error) {
      console.log('Error creating dashboard:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 2. Update a dashboard
export const updateDashboard = createAsyncThunk(
  'dashboard/updateDashboard',
  async ({ dashboard_id, title, description }, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.put('/dashboard/update', {
        dashboard_id,
        title,
        description
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return { dashboard_id, title, description };
    } catch (error) {
      console.log('Error updating dashboard:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 3. Delete a dashboard
export const deleteDashboard = createAsyncThunk(
  'dashboard/deleteDashboard',
  async (dashboard_id, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.delete('/dashboard/delete', {
        data: { dashboard_id }
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return { dashboard_id };
    } catch (error) {
      console.log('Error deleting dashboard:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 4. Get all dashboards for a user
export const fetchDashboards = createAsyncThunk(
  'dashboard/fetchDashboards',
  async (user_id, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.get('/dashboard/listAll', {
        params: { user_id }
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return resp.data.dashboards;
    } catch (error) {
      console.log('Error fetching dashboards:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 5. Add a visual to a dashboard
export const addVisual = createAsyncThunk(
  'dashboard/addVisual',
  async ({ dashboard_id, message_id, chart_id, type }, { rejectWithValue }) => {
    // console.log('Adding visual to dashboard:', { dashboard_id, message_id, chart_id, type });
    try {
      const resp = await axiosInstance.post('/dashboard/visual/addVisual', {
        dashboard_id,
        message_id,
        chart_id,
        type
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return resp.data.visual;
    } catch (error) {
      console.log('Error adding visual:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 6. Get all visuals for a dashboard
export const fetchVisuals = createAsyncThunk(
  'dashboard/fetchVisuals',
  async (dashboard_id, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.get('/dashboard/visual/listAll', {
        params: { dashboard_id }
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      // console.log('Fetch visuals response:', resp.data);
      // Handle both response formats - when visuals are nested and when they are direct
      const visuals = resp.data.visuals || resp.data;
      return { dashboard_id, visuals };
    } catch (error) {
      console.log('Error fetching visuals:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 7. Update layout for visuals
export const updateVisualLayouts = createAsyncThunk(
  'dashboard/updateVisualLayouts',
  async ({ dashboard_id, visuals }, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.put('/dashboard/visual/update', {
        dashboard_id,
        visuals
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return { dashboard_id, visuals };
    } catch (error) {
      console.log('Error updating visual layouts:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 8. Delete a visual
export const deleteVisual = createAsyncThunk(
  'dashboard/deleteVisual',
  async (visual_id, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.delete('/dashboard/visual/delete', {
        data: { visual_id }
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return { visual_id };
    } catch (error) {
      console.log('Error deleting visual:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 9. Refresh a single visual (fetch updated chart_code)
export const refreshVisualById = createAsyncThunk(
  'dashboard/refreshVisualById',
  async (visual_id, { rejectWithValue }) => {
    try {
      // console.log('Refreshing visual:', visual_id);
      const resp = await fetch(`${REFRESH_SERVICE_URL}?visual_id=${encodeURIComponent(visual_id)}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      if (resp.status !== 200 && resp.status !== 201) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      // console.log('Refresh visual response:', resp);
      // console.log('Refresh visual response:', resp.data);
      const data = await resp.json();
      const chart_code = data?.chart_code || data?.visual?.chart_code || data?.data?.chart_code;
      if (!chart_code) {
        return rejectWithValue('No chart_code in response');
      }
      return { visual_id, chart_code };
    } catch (error) {
      console.log('Error refreshing visual:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 10. Refresh all visuals in a dashboard
export const refreshVisualsForDashboard = createAsyncThunk(
  'dashboard/refreshVisualsForDashboard',
  async (dashboard_id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const visuals = state.dashboard.activeDashboard?.dashboard_id === dashboard_id
        ? (state.dashboard.activeDashboard.visuals || [])
        : [];

      const results = await Promise.all(
        visuals.map(async (v) => {
          try {
            const resp = await fetch(`${REFRESH_SERVICE_URL}?visual_id=${encodeURIComponent(v.visual_id)}`, {
              method: 'GET',
              headers: { 'Accept': 'application/json' },
            });
            if (resp.status !== 200 && resp.status !== 201) {
              return rejectWithValue(`Error: ${resp.status}`);
            }
            // console.log('Refresh visual response:', resp);
            // console.log('Refresh visual response:', resp.data);
            const data = await resp.json();
            const chart_code = data?.chart_code || data?.visual?.chart_code || data?.data?.chart_code || null;
            return { visual_id: v.visual_id, chart_code };
          } catch (e) {
            console.log('Error refreshing visual:', e);
            console.log('Error message:', e.message);
            return { visual_id: v.visual_id, chart_code: null, error: e?.message || 'error' };
          }
        })
      );

      return { dashboard_id, results };
    } catch (error) {
      console.log('Error refreshing visuals for dashboard:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// Sort dashboards by updated_at (most recent first)
const sortDashboards = (dashboards) => {
  return dashboards.sort((a, b) => {
    return new Date(b.updated_at) - new Date(a.updated_at);
  });
};

// Find the closest available position on the grid
const findAvailablePosition = (visuals) => {
  // Start from position (0, 0)
  let maxY = 0;
  
  // Find the maximum y position
  visuals.forEach(visual => {
    if (visual.layout) {
      const bottomY = visual.layout.y + visual.layout.h;
      if (bottomY > maxY) {
        maxY = bottomY;
      }
    }
  });
  
  // Place the new visual below the existing ones
  return { x: 0, y: maxY };
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    dashboardList: [],
    activeDashboard: null, // { dashboard_id, title, description, visuals: [] }
    loading: false,
    error: null,
  },
  reducers: {
    setActiveDashboard: (state, action) => {
      state.activeDashboard = action.payload;
    },
    clearActiveDashboard: (state) => {
      state.activeDashboard = null;
    },
    // Update the layout of a visual locally
    updateLocalLayout: (state, action) => {
      if (state.activeDashboard) {
        const { visual_id, layout } = action.payload;
        const visual = state.activeDashboard.visuals.find(v => v.visual_id === visual_id);
        if (visual) {
          visual.layout = layout;
        }
      }
    }
  },
  extraReducers: (builder) => {
    // fetchDashboards
    builder.addCase(fetchDashboards.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDashboards.fulfilled, (state, action) => {
      state.loading = false;
      state.dashboardList = sortDashboards(action.payload);
    });
    builder.addCase(fetchDashboards.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    
    // createDashboard
    builder.addCase(createDashboard.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createDashboard.fulfilled, (state, action) => {
      state.loading = false;
      state.dashboardList.push(action.payload);
      state.dashboardList = sortDashboards(state.dashboardList);
      state.activeDashboard = action.payload;
      state.activeDashboard.visuals = [];
    });
    builder.addCase(createDashboard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    
    // updateDashboard
    builder.addCase(updateDashboard.fulfilled, (state, action) => {
      const { dashboard_id, title, description } = action.payload;
      const dashboard = state.dashboardList.find(d => d.dashboard_id === dashboard_id);
      if (dashboard) {
        dashboard.title = title;
        dashboard.description = description;
        dashboard.updated_at = new Date().toISOString();
        // Re-sort the list
        state.dashboardList = sortDashboards(state.dashboardList);
      }
      if (state.activeDashboard?.dashboard_id === dashboard_id) {
        state.activeDashboard.title = title;
        state.activeDashboard.description = description;
      }
    });
    
    // deleteDashboard
    builder.addCase(deleteDashboard.fulfilled, (state, action) => {
      const deletedDashboardId = action.payload;
      state.dashboardList = state.dashboardList.filter(
        (dashboard) => dashboard.dashboard_id !== deletedDashboardId
      );
      
      // If the deleted dashboard was the active one, clear it and navigate to home
      if (state.activeDashboard?.dashboard_id === deletedDashboardId) {
        state.activeDashboard = null;
        // Navigate to home page
        window.location.href = '/';
      }
    });
    
    // fetchVisuals
    builder.addCase(fetchVisuals.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchVisuals.fulfilled, (state, action) => {
      state.loading = false;
      const { dashboard_id, visuals } = action.payload;
      if (state.activeDashboard?.dashboard_id === dashboard_id) {
        // Process visuals to ensure they have valid layouts
        const processedVisuals = visuals.map(visual => {
          // If layout values are all 0, assign default layout
          if (visual.layout && 
              visual.layout.w === 0 && 
              visual.layout.h === 0 && 
              visual.layout.x === 0 && 
              visual.layout.y === 0) {
            // Find available position
            const position = findAvailablePosition(visuals);
            return {
              ...visual,
              layout: {
                w: 6, // Default width of 6 columns
                h: 3, // Default height of 3 rows
                x: position.x,
                y: position.y
              }
            };
          }
          return visual;
        });
        
        state.activeDashboard = {
          ...state.activeDashboard,
          visuals: processedVisuals
        };
      }
    });
    builder.addCase(fetchVisuals.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    
    // addVisual
    builder.addCase(addVisual.fulfilled, (state, action) => {
      const newVisual = action.payload;
      if (state.activeDashboard && state.activeDashboard.dashboard_id === newVisual.dashboard_id) {
        // Check if we already have this visual
        const existingIndex = state.activeDashboard.visuals.findIndex(v => v.visual_id === newVisual.visual_id);
        
        if (existingIndex >= 0) {
          // Update the existing visual
          state.activeDashboard.visuals[existingIndex] = newVisual;
        } else {
          // Add new visual with a default layout if needed
          if (!newVisual.layout || 
              (newVisual.layout.w === 0 && 
               newVisual.layout.h === 0 && 
               newVisual.layout.x === 0 && 
               newVisual.layout.y === 0)) {
            const position = findAvailablePosition(state.activeDashboard.visuals);
            newVisual.layout = {
              w: 6, // Default width
              h: 3, // Default height
              x: position.x,
              y: position.y
            };
          }
          
          state.activeDashboard.visuals.push(newVisual);
        }
      }
    });
    
    // updateVisualLayouts
    builder.addCase(updateVisualLayouts.fulfilled, (state, action) => {
      const { dashboard_id, visuals } = action.payload;
      if (state.activeDashboard?.dashboard_id === dashboard_id) {
        // Update the layout for each visual
        visuals.forEach(updateInfo => {
          const visual = state.activeDashboard.visuals.find(v => v.visual_id === updateInfo.visual_id);
          if (visual) {
            visual.layout = updateInfo.layout;
          }
        });
      }
    });
    
    // deleteVisual
    builder.addCase(deleteVisual.fulfilled, (state, action) => {
      const { visual_id } = action.payload;
      if (state.activeDashboard) {
        state.activeDashboard.visuals = state.activeDashboard.visuals.filter(v => v.visual_id !== visual_id);
      }
    });
    
    // refreshVisualById
    builder.addCase(refreshVisualById.pending, (state, action) => {
      const visual_id = action.meta.arg;
      if (state.activeDashboard) {
        const visual = state.activeDashboard.visuals.find(v => v.visual_id === visual_id);
        if (visual) {
          visual.isRefreshing = true;
        }
      }
    });
    builder.addCase(refreshVisualById.fulfilled, (state, action) => {
      const { visual_id, chart_code } = action.payload;
      if (state.activeDashboard) {
        const visual = state.activeDashboard.visuals.find(v => v.visual_id === visual_id);
        if (visual) {
          if (!visual.visual) visual.visual = {};
          visual.visual.chart_code = chart_code;
          visual.isRefreshing = false;
        }
      }
    });
    builder.addCase(refreshVisualById.rejected, (state, action) => {
      const visual_id = action.meta.arg;
      if (state.activeDashboard) {
        const visual = state.activeDashboard.visuals.find(v => v.visual_id === visual_id);
        if (visual) {
          visual.isRefreshing = false;
        }
      }
    });

    // refreshVisualsForDashboard
    builder.addCase(refreshVisualsForDashboard.pending, (state, action) => {
      const dashboard_id = action.meta.arg;
      if (state.activeDashboard?.dashboard_id === dashboard_id) {
        (state.activeDashboard.visuals || []).forEach(v => { v.isRefreshing = true; });
      }
    });
    builder.addCase(refreshVisualsForDashboard.fulfilled, (state, action) => {
      const { dashboard_id, results } = action.payload;
      if (state.activeDashboard?.dashboard_id === dashboard_id) {
        results.forEach(({ visual_id, chart_code }) => {
          const visual = state.activeDashboard.visuals.find(v => v.visual_id === visual_id);
          if (visual) {
            if (chart_code) {
              if (!visual.visual) visual.visual = {};
              visual.visual.chart_code = chart_code;
            }
          }
        });
        // Clear refreshing state for all visuals once all are done
        (state.activeDashboard.visuals || []).forEach(v => { v.isRefreshing = false; });
      }
    });
    builder.addCase(refreshVisualsForDashboard.rejected, (state, action) => {
      const dashboard_id = action.meta.arg;
      if (state.activeDashboard?.dashboard_id === dashboard_id) {
        (state.activeDashboard.visuals || []).forEach(v => { v.isRefreshing = false; });
      }
    });
    
    // Handle logout
    builder.addCase(logout, (state) => {
      state.activeDashboard = null;
      state.dashboardList = [];
    });
  }
});

export const {
  setActiveDashboard,
  clearActiveDashboard,
  updateLocalLayout
} = dashboardSlice.actions;

export default dashboardSlice.reducer; 